import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { CorrectionContextType } from "./types";
import {
    Correction,
    CorrectionAddArg,
    CorrectionStatus,
    SetCorrectionStatusArg,
    SortBy,
} from "../../api/fastApi/commentsTypes";
import {
    correctionAddApi,
    getAllCorrectionsApi,
    getCorrectionsForMediaApi,
    setCorrectionStatusApi,
} from "../../api/fastApi/comments";
import { useParams } from "react-router-dom";
import { ILang } from "../AudioProvider/types";

const CorrectionContext = createContext<CorrectionContextType | undefined>(undefined);

interface CommentProviderProps {
    children: ReactNode;
}

export const CorrectionProvider: React.FC<CommentProviderProps> = ({ children }) => {
    const [corrections, setAllCorrections] = useState<Correction[]>([]);
    const [correctionsForMedia, setCorrectionsForMedia] = useState<Correction[]>([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(10);
    const [status, setStatus] = useState<CorrectionStatus | null>("not_started");
    const [language, setLanguage] = useState<ILang | null>(null);
    const [mediaCorrection, setMediaCorrection] = useState<string>("");
    const [sortBy, setSortBy] = useState<SortBy | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { mediaId } = useParams();

    useEffect(() => {
        if (mediaId) {
            void getCorrectionsForMedia(mediaId);
        }
    }, [mediaId]);

    useEffect(() => {
        fetchCorrections();
    }, [offset, limit, status, language, sortBy, mediaCorrection]);

    async function getCorrectionsForMedia(mediaId: string) {
        console.log("getCorrectionsForMedia");
        try {
            const data = await getCorrectionsForMediaApi({ mediaId });
            setCorrectionsForMedia(data.corrections);
        } catch (err) {
            console.log("err", err);
        }
    }

    const fetchCorrections = async () => {
        setLoading(true);
        setError(null);
        console.log("fetchCorrections", mediaCorrection);
        try {
            const data = await getAllCorrectionsApi({
                offset,
                limit,
                status,
                language,
                media: mediaCorrection,
                sort_by: sortBy,
            });
            if (data.success) {
                setAllCorrections(data.corrections);
                setTotal(data.total);
            } else {
                throw new Error("API returned success: false");
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to fetch corrections";
            setError(errorMessage);
            console.error("Error fetching corrections:", err);
        } finally {
            setLoading(false);
        }
    };

    const updateCorrectionStatus = async ({
        mediaId,
        status,
        correctionId,
    }: SetCorrectionStatusArg) => {
        try {
            const data = await setCorrectionStatusApi({ mediaId, status, correctionId });
            await fetchCorrections();
            await getCorrectionsForMedia(mediaId);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to update correction";
            setError(errorMessage);
            console.error("Error updating correction:", err);
        }
    };

    const addCorrection = async (media: string, body: CorrectionAddArg) => {
        try {
            await correctionAddApi(media, body);
            await fetchCorrections();
            await getCorrectionsForMedia(media);
        } catch (err) {
            console.log("correctionAddApi error", err);
        }
    };

    return (
        <CorrectionContext.Provider
            value={{
                corrections,
                total,
                offset,
                limit,
                status,
                language,
                loading,
                error,
                sortBy,
                mediaCorrection,
                correctionsForMedia,
                setTotal,
                setOffset,
                setLimit,
                setStatus,
                setLoading,
                setError,
                fetchCorrections,
                updateCorrectionStatus,
                addCorrection,
                setLanguage,
                setMediaCorrection,
                setSortBy,
            }}
        >
            {children}
        </CorrectionContext.Provider>
    );
};

export const useCorrection = (): CorrectionContextType => {
    const context = useContext(CorrectionContext);
    if (!context) {
        throw new Error("useCorrection must be used within a CorrectionProvider");
    }
    return context;
};
